// Settings (admin): the website the lead agent reads to learn what we sell, the brochures it may offer leads, and the
// Outlook app and address lead emails are sent as.
import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { useWatch } from "react-hook-form";
import { AutocompleteArrayInput } from "@/components/admin/autocomplete-array-input";
import { BooleanInput } from "@/components/admin/boolean-input";
import { NumberInput } from "@/components/admin/number-input";
import { ArrayInput } from "@/components/admin/array-input";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextInput } from "@/components/admin/text-input";
import { Card, CardContent } from "@/components/ui/card";

import {
  DEFAULT_OUR_WEBSITE,
  type NightPlan,
} from "../../../../../supabase/functions/_shared/leadAgent.ts";
import {
  SEND_DAYS,
  validBookingUrl,
  validSendHours,
  validTimezone,
} from "../../settings/outreachSettings";
import { DEFAULT_SEND_AS, SENDERS } from "./outlook";

const P = "resources.pre_leads.settings";

// Read-only: when the server last polled the mailboxes (latest outreach_log row with step "poll").
const LastPoll = () => {
  const translate = useTranslate();
  const { data } = useGetList("outreach_log", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "at", order: "DESC" },
    filter: { step: "poll" },
  });
  const row = data?.[0];
  const errors = Number(row?.detail?.errors) || 0;
  return (
    <p className="text-sm text-muted-foreground">
      {row
        ? translate(`${P}.last_poll`, {
            time: new Date(row.at).toLocaleString(),
          })
        : translate(`${P}.never_polled`)}
      {errors > 0 &&
        ` ${translate(`${P}.poll_errors`, { smart_count: errors })}`}
    </p>
  );
};

export const LeadAgentSettings = () => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const sendAs = useWatch({ name: "outlook_send_as" }) as string | undefined;
  const nightPlan = useWatch({ name: "lead_night_plan" }) as
    | NightPlan
    | undefined;
  return (
    <Card id="lead-agent">
      <CardContent className="space-y-4">
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("resources.leads.agent.settings_title")}
        </h2>
        <TextInput
          source="our_website"
          label="resources.leads.agent.our_website"
          helperText="resources.leads.agent.our_website_help"
          placeholder={DEFAULT_OUR_WEBSITE}
        />
        <h3 className="text-lg font-medium text-muted-foreground">
          {translate("resources.leads.agent.brochures")}
        </h3>
        <p className="text-sm text-muted-foreground">
          {translate("resources.leads.agent.brochures_help")}
        </p>
        <ArrayInput source="lead_brochures" label={false} helperText={false}>
          <SimpleFormIterator disableReordering disableClear inline>
            <TextInput
              source="name"
              label="resources.leads.agent.brochure_name"
              helperText={false}
            />
            <TextInput
              source="url"
              label="resources.leads.agent.brochure_url"
              helperText={false}
            />
            <TextInput
              source="about"
              label="resources.leads.agent.brochure_about"
              helperText={false}
            />
          </SimpleFormIterator>
        </ArrayInput>
        <h3 className="text-lg font-medium text-muted-foreground">
          {translate("resources.leads.agent.night.title")}
        </h3>
        <p className="text-sm text-muted-foreground">
          {nightPlan?.segments?.length
            ? translate("resources.leads.agent.night.plan", {
                segments: nightPlan.segments.map((s) => s.name).join(", "),
              })
            : translate("resources.leads.agent.night.no_plan")}
        </p>
        <BooleanInput
          source="lead_night_find"
          label="resources.leads.agent.night.find"
          helperText="resources.leads.agent.night.find_help"
        />
        <BooleanInput
          source="lead_night_send"
          label="resources.leads.agent.night.send"
          helperText="resources.leads.agent.night.send_help"
        />
        <h3 className="text-lg font-medium text-muted-foreground">
          {translate("resources.leads.agent.outlook_title")}
        </h3>
        <p className="text-sm text-muted-foreground">
          {translate("resources.leads.agent.outlook_help")}
        </p>
        <TextInput
          source="outlook_client_id"
          label="resources.leads.agent.outlook_client_id"
          helperText="resources.leads.agent.outlook_client_id_help"
        />
        <TextInput
          source="outlook_send_as"
          label="resources.leads.agent.outlook_send_as"
          helperText={false}
          placeholder={DEFAULT_SEND_AS}
        />
        <SelectInput
          source="outlook_default_from"
          label="resources.leads.agent.default_from"
          helperText="resources.leads.agent.default_from_help"
          choices={SENDERS.map((id) => ({
            id,
            name: translate(`resources.leads.agent.senders.${id}`, {
              address: sendAs || DEFAULT_SEND_AS,
              name: identity?.fullName ?? "",
            }),
          }))}
          optionText="name"
        />
        <TextInput
          source="signature_personal"
          label="resources.leads.agent.signature_personal"
          helperText="resources.leads.agent.signature_help"
          placeholder={"{name}\nCEO, THNK Consultancy\nwww.ithnk.com"}
          multiline
        />
        <TextInput
          source="signature_sales"
          label="resources.leads.agent.signature_sales"
          helperText="resources.leads.agent.signature_help"
          placeholder={"THNK Consultancy\nsales@ithnk.com · www.ithnk.com"}
          multiline
        />
        <h3 className="text-lg font-medium text-muted-foreground">
          {translate(`${P}.title`)}
        </h3>
        <p className="text-sm text-muted-foreground">
          {translate(`${P}.help`)}
        </p>
        <NumberInput
          source="outreach_max_touches"
          label={`${P}.max_touches`}
          helperText={`${P}.max_touches_help`}
          min={1}
          max={3}
        />
        <TextInput
          source="outreach_followup_days_text"
          label={`${P}.followup_days`}
          helperText={`${P}.followup_days_help`}
        />
        <AutocompleteArrayInput
          source="outreach_send_days"
          label={`${P}.send_days`}
          helperText={`${P}.send_days_help`}
          choices={SEND_DAYS.map(({ id, name }) => ({
            id,
            name: `${P}.days.${name}`,
          }))}
        />
        <TextInput
          source="outreach_send_hours"
          label={`${P}.send_hours`}
          helperText={`${P}.send_hours_help`}
          validate={(v: string) =>
            validSendHours(v) ? undefined : `${P}.send_hours_invalid`
          }
        />
        <TextInput
          source="outreach_timezone"
          label={`${P}.timezone`}
          helperText={`${P}.timezone_help`}
          validate={(v: string) =>
            validTimezone(v) ? undefined : `${P}.timezone_invalid`
          }
        />
        <NumberInput
          source="outreach_daily_cap"
          label={`${P}.daily_cap`}
          helperText={`${P}.daily_cap_help`}
          min={1}
        />
        <TextInput
          source="outreach_opt_out_line"
          label={`${P}.opt_out_line`}
          helperText={`${P}.opt_out_line_help`}
          multiline
        />
        <TextInput
          source="booking_url"
          label={`${P}.booking_url`}
          helperText={`${P}.booking_url_help`}
          placeholder="https://"
          validate={(v: string) =>
            validBookingUrl(v) ? undefined : `${P}.booking_url_invalid`
          }
        />
        <LastPoll />
      </CardContent>
    </Card>
  );
};
